import Flag from '../marks/Flag'
import { america } from '@/content/missions'
import type { HeritageArt as ArtName } from '@/content/heritage'

// Illustrations for the heritage timeline: drawn here, or the public domain
// mark the moment is about (the Great Seal, the park arrowhead, the NASA worm,
// FirstGov, the U.S. Digital Service, USWDS and America by Design).

/** A flag with thirteen stars in a ring, or forty-eight in six rows of eight. */
function EarlyFlag({ stars: count }: { stars: 13 | 48 }) {
  const stars =
    count === 13
      ? Array.from({ length: 13 }, (_, i) => {
          const a = ((i / 13) * 360 - 90) * (Math.PI / 180)
          return [38 + 17 * Math.cos(a), 27 + 17 * Math.sin(a)]
        })
      : Array.from({ length: 48 }, (_, i) => [7 + (i % 8) * 8.9, 6 + Math.floor(i / 8) * 8.4])
  const star = (cx: number, cy: number) =>
    Array.from({ length: 10 }, (_, i) => {
      const a = ((-90 + i * 36) * Math.PI) / 180
      const r = (i % 2 === 0 ? 3.2 : 1.25) * (count === 13 ? 1 : 0.8)
      return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`
    }).join(' ')
  return (
    <svg viewBox="0 0 190 100" aria-hidden="true" className="heritage-art__flag">
      <rect width="190" height="100" fill="#fff" />
      {Array.from({ length: 7 }, (_, i) => (
        <rect key={i} y={(i * 2 * 100) / 13} width="190" height={100 / 13} fill="#b31942" />
      ))}
      <rect width="76" height={700 / 13} fill="#0a3161" />
      {stars.map(([x, y], i) => (
        <polygon key={i} points={star(x, y)} fill="#fff" />
      ))}
    </svg>
  )
}

function Unigrid() {
  return (
    <svg viewBox="0 0 160 200" aria-hidden="true" className="heritage-art__panel">
      <rect width="160" height="200" fill="var(--mds-raised)" />
      <rect width="160" height="34" fill="var(--mds-ink)" />
      <rect x="10" y="12" width="70" height="6" rx="1" fill="var(--mds-bg)" />
      <rect x="10" y="22" width="44" height="4" rx="1" fill="var(--mds-bg)" opacity="0.6" />
      {[0, 1, 2, 3].map((c) =>
        [0, 1, 2].map((r) => (
          <rect key={`${c}-${r}`} x={10 + c * 36} y={46 + r * 50} width="32" height="44" fill="none" stroke="var(--mds-border-strong)" strokeWidth="0.75" />
        )),
      )}
      <rect x="10" y="46" width="68" height="94" fill="var(--mds-primary)" opacity="0.85" />
      <rect x="82" y="46" width="68" height="4" fill="var(--mds-ink)" />
      {[56, 62, 68, 74, 80, 86].map((y) => (
        <rect key={y} x="82" y={y} width={y % 12 ? 60 : 50} height="2" fill="var(--mds-ink-muted)" />
      ))}
    </svg>
  )
}

function Swatches() {
  return (
    <svg viewBox="0 0 200 140" aria-hidden="true" className="heritage-art__panel">
      <rect width="200" height="140" fill="var(--mds-raised)" />
      <text x="16" y="70" style={{ fontFamily: 'var(--mds-font-sans)', fontSize: 64, fontWeight: 700, letterSpacing: '-0.05em' }} fill="var(--mds-ink)">
        Aa
      </text>
      {['#b31942', '#0a3161', '#000c1f', '#f7f7f7'].map((color, i) => (
        <rect key={color} x={16 + i * 44} y="92" width="38" height="30" rx="3" fill={color} stroke="var(--mds-border)" />
      ))}
      <rect x="118" y="24" width="66" height="4" fill="var(--mds-ink)" />
      <rect x="118" y="34" width="52" height="3" fill="var(--mds-ink-muted)" />
      <rect x="118" y="42" width="58" height="3" fill="var(--mds-ink-muted)" />
    </svg>
  )
}

function Access() {
  return (
    <svg viewBox="0 0 120 120" aria-hidden="true" className="heritage-art__glyph">
      <circle cx="60" cy="60" r="56" fill="var(--mds-primary)" />
      <circle cx="60" cy="60" r="44" fill="none" stroke="var(--mds-on-primary)" strokeWidth="4" />
      <circle cx="60" cy="34" r="7" fill="var(--mds-on-primary)" />
      <path d="M34 48h52M60 48v24M60 72l-14 22M60 72l14 22" stroke="var(--mds-on-primary)" strokeWidth="6" strokeLinecap="round" fill="none" />
    </svg>
  )
}

function Plain() {
  return (
    <svg viewBox="0 0 200 140" aria-hidden="true" className="heritage-art__panel">
      <rect width="200" height="140" fill="var(--mds-raised)" />
      {[24, 40, 56, 72, 88, 104].map((y, i) => (
        <rect key={y} x="18" y={y} width={[164, 150, 160, 120, 156, 96][i]} height="6" rx="3" fill={i === 2 ? 'var(--mds-accent)' : 'var(--mds-border-strong)'} opacity={i === 2 ? 0.9 : 0.6} />
      ))}
      <text x="18" y="16" style={{ fontFamily: 'var(--mds-font-mono)', fontSize: 9 }} fill="var(--mds-ink-muted)">
        SAY IT PLAINLY
      </text>
    </svg>
  )
}

function Phone() {
  return (
    <svg viewBox="0 0 120 180" aria-hidden="true" className="heritage-art__panel heritage-art__panel--tall">
      <rect x="10" y="4" width="100" height="172" rx="16" fill="var(--mds-raised)" stroke="var(--mds-ink)" strokeWidth="3" />
      <rect x="44" y="12" width="32" height="5" rx="2.5" fill="var(--mds-ink)" />
      <rect x="22" y="30" width="60" height="7" rx="2" fill="var(--mds-ink)" />
      <rect x="22" y="44" width="76" height="4" rx="2" fill="var(--mds-ink-muted)" />
      <rect x="22" y="52" width="66" height="4" rx="2" fill="var(--mds-ink-muted)" />
      <rect x="22" y="70" width="76" height="22" rx="11" fill="var(--mds-primary)" />
      <rect x="22" y="104" width="76" height="56" rx="8" fill="var(--mds-surface)" />
    </svg>
  )
}

export default function HeritageArt({ art }: { art: ArtName }) {
  switch (art) {
    case 'flag13':
      return <EarlyFlag stars={13} />
    case 'flag48':
      return <EarlyFlag stars={48} />
    case 'park':
      return <img src="/seals/nps.svg" alt="" className="heritage-art__seal" />
    case 'orbit':
      return <img src="/media/heritage-nasa-worm.svg" alt="" className="heritage-art__worm" />
    case 'uswds':
      return <span className="heritage-art__uswds" aria-hidden="true" />
    case 'usds':
      return <img src="/media/heritage-usds.svg" alt="" className="heritage-art__usds" />
    case 'seal':
      return <img src={`/${america.seal!.file.replace(/^public\//, '')}`} alt="" className="heritage-art__seal" />
    case 'flag50':
      return <Flag variant="official" className="heritage-art__flag50" />
    case 'grid':
      return <Unigrid />
    case 'type':
      return <Swatches />
    case 'access':
      return <Access />
    case 'plain':
      return <Plain />
    case 'browser':
      return (
        <span className="heritage-art__plate">
          <img src="/media/heritage-firstgov.png" alt="" />
        </span>
      )
    case 'phone':
      return <Phone />
    case 'america-by-design':
      return <span className="heritage-art__abd" aria-hidden="true" />
    case 'america-gov':
      return (
        <span className="heritage-art__ask" aria-hidden="true">
          <span className="heritage-art__ask-hello">Hello, America</span>
          <span className="heritage-art__ask-field">Describe what you need</span>
        </span>
      )
  }
}
